import React, { useState, useEffect } from 'react';
import { 
  Search, 
  FileText, 
  Download, 
  Mail, 
  Book, 
  User, 
  Filter, 
  Info 
} from 'lucide-react';
import { getArticles, getCategories, downloadArticle } from './api';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

const AppliedAIJournal = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getArticles(currentPage, 10, selectedCategory, searchTerm);
        setArticles(result.articles);
        setTotalPages(result.totalPages);
        
        const categoriesData = await getCategories();
        setCategories(categoriesData);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load articles. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, selectedCategory, searchTerm]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  const handleDownload = (id) => {
    downloadArticle(id);
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your message! We will get back to you soon.');
    setContactForm({ name: '', email: '', message: '' });
  };

  const renderPagination = () => {
    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 mr-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="px-3 py-1">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 ml-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  // Function to open PDF in a new window
  const viewPDF = (pdfFilename) => {
    const url = `${BASE_URL}/uploads/${pdfFilename}`;
    window.open(url, '_blank');
  };

  return (
    <div className="container mx-auto p-6">
      <header className="flex flex-col md:flex-row justify-between items-center mb-8 bg-blue-900 text-white p-4 rounded-lg">
        <div>
          <h1 className="text-3xl font-bold">Applied Artificial Intelligence Journal</h1>
          <p className="text-gray-300">Advancing AI Research and Applications</p>
        </div>
        <nav className="flex space-x-4 mt-4 md:mt-0">
          <a href="#home" className="hover:text-blue-300">Home</a>
          <a href="#articles" className="hover:text-blue-300">Articles</a>
          <a href="#about" className="hover:text-blue-300">About</a>
          <a href="#contact" className="hover:text-blue-300">Contact</a>
        </nav>
      </header>

      <section id="home" className="mb-12 bg-blue-50 p-6 rounded-lg">
        <div className="flex flex-col items-center text-center">
          <Book size={48} className="text-blue-700 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Welcome to Applied AI Journal</h2>
          <p className="max-w-2xl text-gray-600">
            The Applied Artificial Intelligence Journal publishes peer-reviewed research focusing on
            practical applications of artificial intelligence across various domains. Our goal is to
            bridge the gap between theoretical AI research and real-world implementation.
          </p>
        </div>
      </section>

      <section id="articles" className="mb-12">
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="bg-blue-800 text-white p-4 flex items-center">
            <FileText className="mr-2" /> 
            <h2 className="text-xl font-bold">Article Database</h2>
          </div>
          
          <div className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row mb-4 gap-2">
              <input 
                type="text"
                placeholder="Search articles..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-grow p-2 border rounded"
              />
              <select 
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="p-2 border rounded"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <button 
                type="submit" 
                className="p-2 bg-blue-600 text-white rounded flex items-center justify-center"
              >
                <Search size={18} className="mr-1" /> Search
              </button>
            </form>

            {loading ? (
              <div className="text-center py-8">Loading articles...</div>
            ) : error ? (
              <div className="text-center py-8 text-red-500">{error}</div>
            ) : articles.length === 0 ? (
              <div className="text-center py-8">No articles found. Try different search criteria.</div>
            ) : (
              <>
                {articles.map(article => (
                  <div 
                    key={article._id} 
                    className="border-b py-4 flex flex-col md:flex-row justify-between md:items-center"
                  >
                    <div className="mb-4 md:mb-0 md:pr-4">
                      <h3 className="font-bold text-lg text-blue-800">{article.title}</h3>
                      <p className="text-gray-600">{article.authors.join(', ')}</p>
                      <p className="text-sm text-gray-700 mt-2">{article.abstract.slice(0, 150)}...</p>
                      <div className="flex items-center text-sm text-gray-500 mt-1">
                        <span className="mr-4">
                          {new Date(article.publicationDate).toLocaleDateString()}
                        </span>
                        <span className="mr-4">Category: {article.category}</span>
                        <span>Downloads: {article.downloads}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        className="px-3 py-2 border border-blue-600 text-blue-600 rounded flex items-center"
                        onClick={() => viewPDF(article.pdfFilename)}
                      >
                        <FileText size={16} className="mr-1" /> View PDF
                      </button>
                      <button 
                        className="px-3 py-2 bg-blue-600 text-white rounded flex items-center"
                        onClick={() => handleDownload(article._id)}
                      >
                        <Download size={16} className="mr-1" /> Download PDF
                      </button>
                    </div>
                  </div>
                ))}
                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </section>

      <section id="about" className="mb-12">
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="bg-blue-800 text-white p-4 flex items-center">
            <Info className="mr-2" /> 
            <h2 className="text-xl font-bold">About the Journal</h2>
          </div>
          
          <div className="p-6">
            <h3 className="text-xl font-semibold mb-3">Our Mission</h3>
            <p className="mb-4">
              The Applied Artificial Intelligence Journal is dedicated to publishing cutting-edge research 
              in the field of applied AI. We focus on practical applications, implementations, and 
              real-world impacts of artificial intelligence technologies.
            </p>
            <h3 className="text-xl font-semibold mb-3">Editorial Board</h3>
            <p className="mb-4">
              Our editorial board consists of leading researchers and practitioners in the field of 
              artificial intelligence, ensuring the highest quality of published articles through a 
              rigorous peer-review process.
            </p>
            <h3 className="text-xl font-semibold mb-3">Publication Ethics</h3>
            <p>
              We are committed to maintaining the highest standards of publication ethics and follow 
              the guidelines set by the Committee on Publication Ethics (COPE). All articles undergo 
              a thorough review process to ensure originality, validity, and significance.
            </p>
          </div>
        </div>
      </section>

      <section id="contact">
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="bg-blue-800 text-white p-4 flex items-center">
            <Mail className="mr-2" /> 
            <h2 className="text-xl font-bold">Contact Us</h2>
          </div>
          <div className="p-6">
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block mb-1">Name</label>
                <input 
                  type="text"
                  name="name"
                  value={contactForm.name}
                  onChange={handleContactChange}
                  className="w-full p-2 border rounded" 
                  placeholder="Your Name"
                  required
                />
              </div>
              <div>
                <label className="block mb-1">Email</label>
                <input 
                  type="email"
                  name="email"
                  value={contactForm.email}
                  onChange={handleContactChange}
                  className="w-full p-2 border rounded" 
                  placeholder="Your Email"
                  required
                />
              </div>
              <div>
                <label className="block mb-1">Message</label>
                <textarea 
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  className="w-full border p-2 rounded" 
                  placeholder="Your message..."
                  rows={4}
                  required
                />
              </div>
              <button 
                type="submit" 
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="mt-12 p-6 bg-gray-800 text-white rounded-lg">
        <div className="flex flex-col md:flex-row justify-between">
          <div>
            <h3 className="text-lg font-bold mb-2">Applied Artificial Intelligence Journal</h3>
            <p className="text-gray-400">Advancing the field of AI through practical research</p>
          </div>
          <div className="mt-4 md:mt-0">
            <h4 className="font-semibold mb-2">Quick Links</h4>
            <ul className="space-y-1">
              <li><a href="#home" className="text-gray-400 hover:text-white">Home</a></li>
              <li><a href="#articles" className="text-gray-400 hover:text-white">Article Database</a></li>
              <li><a href="#about" className="text-gray-400 hover:text-white">About</a></li>
              <li><a href="#contact" className="text-gray-400 hover:text-white">Contact</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-700 text-center text-gray-400">
          &copy; {new Date().getFullYear()} Applied Artificial Intelligence Journal. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default AppliedAIJournal;
